// Une carte de ticket dans la liste maître : rail de couleur de la catégorie à gauche, titre,
// chips d'état/catégorie, puis une ligne méta resserrée (créateur, assignés, compteurs).
// L'emplacement « non lu » est branché sur useTicketUnread : inactif aujourd'hui, prêt pour le
// futur système de notifications.
import React from "react";
import { FiLink, FiMessageSquare } from "react-icons/fi";
import { UserAvatar } from "../../Components/UserAvatar";
import { categoryTone } from "./ticketUi";
import { useTicketUnread } from "./useTicketUnread";
import { CategoryChip, RelativeTime, StatusPill } from "./TicketBits";
import type { Ticket } from "./ticketsClient";

/** Pile d'avatars des assignés (3 max + « +n »), ou « Non assigné » en sourdine. */
const AssigneeStack: React.FC<{ ticket: Ticket }> = ({ ticket }) => {
	const resolved =
		ticket.assigneesResolved && ticket.assigneesResolved.length > 0
			? ticket.assigneesResolved.map((a) => ({ key: a.id ?? a.email ?? a.name, name: a.name, id: a.id ?? undefined, pictureUrl: a.pictureUrl, avatarKey: a.avatarKey }))
			: ticket.assignees.map((e) => ({ key: e, name: e, id: undefined as string | undefined, pictureUrl: undefined as string | null | undefined, avatarKey: undefined as string | null | undefined }));
	if (resolved.length === 0) {
		return <span className="text-[11px] text-slate-400">Non assigné</span>;
	}
	const shown = resolved.slice(0, 3);
	const extra = resolved.length - shown.length;
	return (
		<span className="flex min-w-0 items-center gap-1.5" title={`Assigné à ${resolved.map((r) => r.name).join(", ")}`}>
			<span className="flex shrink-0 -space-x-1.5">
				{shown.map((a) => (
					<UserAvatar key={a.key} name={a.name} id={a.id} avatarKey={a.avatarKey} pictureUrl={a.pictureUrl} sizeClass="h-5 w-5" textClass="text-[8px]" ringClass="ring-2 ring-white" />
				))}
				{extra > 0 && (
					<span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-[8px] font-bold text-slate-600 ring-2 ring-white">
						+{extra}
					</span>
				)}
			</span>
			<span className="truncate text-[11px] font-medium text-slate-500">
				{resolved.length === 1 ? resolved[0].name : `${resolved.length} assignés`}
			</span>
		</span>
	);
};

export const TicketListCard: React.FC<{
	ticket: Ticket;
	selected: boolean;
	categoryLabel: (key: string) => string;
	onSelect: (id: string) => void;
}> = ({ ticket, selected, categoryLabel, onSelect }) => {
	const tone = categoryTone(ticket.category);
	const unread = useTicketUnread(ticket.id);
	return (
		<button
			type="button"
			onClick={() => onSelect(ticket.id)}
			aria-current={selected ? "true" : undefined}
			className={`group block w-full rounded-xl border border-l-4 bg-white p-3.5 text-left shadow-sm transition hover:-translate-y-px hover:shadow-md ${tone.rail} ${
				selected ? "border-[#005CA9] bg-sky-50/50 ring-1 ring-[#005CA9]/25" : "border-slate-200 hover:border-slate-300"
			}`}
		>
			<div className="flex items-start justify-between gap-2">
				<p className={`min-w-0 truncate text-sm font-semibold ${selected ? "text-[#005CA9]" : "text-slate-800"}`}>
					{unread && <span className="mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-[#005CA9] align-middle" aria-label="Non lu" />}
					{ticket.title}
				</p>
				<RelativeTime value={ticket.updatedAt} className="shrink-0 text-[11px] text-slate-400" />
			</div>
			<div className="mt-1.5 flex flex-wrap items-center gap-1.5">
				<StatusPill status={ticket.status} />
				<CategoryChip categoryKey={ticket.category} label={categoryLabel(ticket.category)} />
			</div>
			<div className="mt-2.5 flex items-center gap-2.5 border-t border-slate-100 pt-2">
				<UserAvatar
					name={ticket.createdBy?.name ?? ticket.createdByEmail}
					id={ticket.createdBy?.id ?? undefined}
					avatarKey={ticket.createdBy?.avatarKey}
					pictureUrl={ticket.createdBy?.pictureUrl}
					sizeClass="h-5 w-5"
					textClass="text-[8px]"
					title={`Créé par ${ticket.createdBy?.name ?? ticket.createdByEmail}`}
				/>
				<AssigneeStack ticket={ticket} />
				<span className="ml-auto inline-flex shrink-0 items-center gap-2.5 text-[11px] text-slate-400">
					{(ticket.linkedContents?.length ?? 0) > 0 && (
						<span className="inline-flex items-center gap-1" title={`${ticket.linkedContents!.length} contenu(s) lié(s)`}>
							<FiLink className="h-3 w-3" /> {ticket.linkedContents!.length}
						</span>
					)}
					{ticket.commentCount > 0 && (
						<span className="inline-flex items-center gap-1" title={`${ticket.commentCount} commentaire(s)`}>
							<FiMessageSquare className="h-3 w-3" /> {ticket.commentCount}
						</span>
					)}
				</span>
			</div>
		</button>
	);
};

export default TicketListCard;
